/** Helpers partagés pour l'affichage des wallet_movements côté marchand.
 *
 * Utilisés par StatementsPage + HistoryPage pour garder un rendu cohérent
 * des Bank Transfers (agrégation par jour, description normalisée, ref
 * synthétique masquée).
 */
import type { WalletMovementFull } from "@/lib/api";

/** Description affichée pour tous les rows Bank Transfer (single ou agrégé). */
export const BANK_TRANSFER_DESCRIPTION = "Virement en banque TMB";

/** True si le mouvement est un Bank Transfer (W2B mirroré). */
export function isBankTransfer(m: WalletMovementFull): boolean {
  return m.type === "manual_adjustment" && m.reference_id.startsWith("aviation-w2b-");
}

/** True si le reference_id est celui d'un row Bank Transfer agrégé (synthétique). */
export function isAggregatedBankTransferRef(referenceId: string): boolean {
  return referenceId.startsWith("aviation-w2b-agg-");
}

/**
 * Agrège les Bank Transfer par jour civil. Les autres types de mouvements
 * (credit_payment, debit_refund, etc.) sont passés en l'état.
 *
 * - opts.acrossWallets = true : 1 wire bancaire = somme drains tous wallets
 *   confondus (Cards + MM) le même jour. balance_after_minor mis à 0 (perd
 *   son sens quand mix wallets).
 * - opts.acrossWallets = false : 1 ligne par (wallet, jour). balance_after
 *   du drain le plus récent conservé.
 */
export function aggregateBankTransfersByDay(
  movements: WalletMovementFull[],
  opts: { acrossWallets: boolean } = { acrossWallets: false },
): WalletMovementFull[] {
  const nonBT = movements.filter((m) => !isBankTransfer(m));
  const bt = movements.filter(isBankTransfer);
  if (bt.length === 0) return movements;

  const buckets = new Map<string, WalletMovementFull[]>();
  for (const m of bt) {
    const day = m.created_at.slice(0, 10);
    const key = opts.acrossWallets
      ? `all|${day}|${m.currency}`
      : `${m.wallet_id}|${day}|${m.currency}`;
    const arr = buckets.get(key) ?? [];
    arr.push(m);
    buckets.set(key, arr);
  }

  const aggregated: WalletMovementFull[] = [];
  for (const [key, rows] of buckets.entries()) {
    if (rows.length === 1) {
      aggregated.push({ ...rows[0], description: BANK_TRANSFER_DESCRIPTION });
      continue;
    }
    const sorted = [...rows].sort((a, b) => a.created_at.localeCompare(b.created_at));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const day = first.created_at.slice(0, 10);
    aggregated.push({
      ...first,
      id: `bt-agg-${key}`,
      reference_id: `aviation-w2b-agg-${day}`,
      created_at: last.created_at,
      amount_minor: sorted.reduce((s, r) => s + r.amount_minor, 0),
      balance_after_minor: opts.acrossWallets ? 0 : last.balance_after_minor,
      description: BANK_TRANSFER_DESCRIPTION,
    });
  }
  return [...nonBT, ...aggregated];
}
